import React, { useState, useEffect, useMemo } from 'react'
import {
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
    Modal,
    Slide,
    Paper,
    IconButton,
    Typography,
    useTheme,
    Button,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { GetApp, Settings } from '@material-ui/icons'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import { Container } from '../../components'
import { SearchField } from '../../components/SearchField'
import { useUser } from '../../hooks'
import { useUserTags } from '../../contexts/UserTagsContext'
import { useMoveInSurvey, DateFilterParams } from '../../hooks/useMoveInSurvey'
import { format as formatDate } from 'date-fns'
import { PortfolioTable } from './PortfolioTable'
import { SurveyDetailContent } from './SurveyDetail'
import { QuestionConfigModal } from './QuestionConfigModal'
import { typographyStyle } from '../../theme/typographyStyle'

export const MoveInSurveyRoot: React.FC = () => {
    const theme = useTheme()
    const { organization } = useUser()
    const { userTags, loading: tagsLoading } = useUserTags()

    const {
        loading,
        summaryList,
        detail,
        detailLoading,
        aiSummary,
        setAiSummary,
        aiSummaryLoading,
        getPortfolioSummary,
        getApartmentDetail,
        exportApartmentDetail,
        getAiSummary,
        setDetail,
    } = useMoveInSurvey()

    const currentYear = new Date().getFullYear()
    const [modalOpen, setModalOpen] = useState(false)
    const [aiModalOpen, setAiModalOpen] = useState(false)
    const [aiModalPropertyName, setAiModalPropertyName] = useState('')
    const [questionConfigOpen, setQuestionConfigOpen] = useState(false)
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
    const [searchText, setSearchText] = useState('')
    const [modalSearchText, setModalSearchText] = useState('')
    const [startDate, setStartDate] = useState<Date | null>(
        new Date(currentYear, 0, 1),
    )
    const [endDate, setEndDate] = useState<Date | null>(
        new Date(currentYear, 11, 31),
    )

    const buildDateParams = (): DateFilterParams | undefined => {
        if (!startDate || !endDate) return undefined
        return {
            start_date: formatDate(startDate, 'yyyy-MM-dd'),
            end_date: formatDate(endDate, 'yyyy-MM-dd'),
        }
    }

    const fetchSummary = () => {
        getPortfolioSummary(organization ?? undefined, buildDateParams())
    }

    useEffect(() => {
        fetchSummary()
    }, [organization, startDate, endDate])

    const filteredSummaryList = useMemo(() => {
        let filtered = summaryList

        if (selectedTagIds.length > 0) {
            filtered = filtered.filter((row) => {
                const relevantTags = userTags.filter((tag) =>
                    tag.apartments.includes(row.apartment_id),
                )
                return relevantTags.some((tag) =>
                    selectedTagIds.includes(tag.id),
                )
            })
        }

        if (searchText.trim()) {
            const query = searchText.trim().toLowerCase()
            filtered = filtered.filter((row) =>
                row.apartment_name.toLowerCase().includes(query),
            )
        }

        return filtered
    }, [summaryList, selectedTagIds, userTags, searchText])

    const handleSelectProperty = (apartmentId: number) => {
        setModalOpen(true)
        getApartmentDetail(apartmentId, buildDateParams())
    }

    const handleCloseModal = () => {
        setModalOpen(false)
        setDetail(null)
        setModalSearchText('')
        setAiSummary(null)
    }

    const handleAnalyze = (apartmentId: number) => {
        const property = summaryList.find((r) => r.apartment_id === apartmentId)
        setAiModalPropertyName(property?.apartment_name ?? '')
        setAiModalOpen(true)
        getAiSummary(apartmentId, buildDateParams())
    }

    const handleCloseAiModal = () => {
        setAiModalOpen(false)
        setAiSummary(null)
    }

    const sentimentColor = (sentiment: string) => {
        switch (sentiment) {
            case 'positive':
                return theme.palette.success.main
            case 'mixed':
                return theme.palette.warning.main
            case 'negative':
                return theme.palette.error.main
            default:
                return theme.palette.grey[500]
        }
    }

    return (
        <Container direction="column" style={{ flex: 1 }}>
            {/* Page Header */}
            <Container
                style={{
                    marginTop: theme.spacing(2),
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: theme.spacing(0, 2),
                    flexWrap: 'wrap',
                    gap: theme.spacing(1),
                }}
            >
                <Typography
                    variant="h5"
                    style={{
                        ...typographyStyle(theme.typography.h5),
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        whiteSpace: 'nowrap',
                    }}
                >
                    Move-In Survey Results
                </Typography>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                        flexWrap: 'wrap',
                        overflow: 'visible',
                    }}
                >
                    <KeyboardDatePicker
                        value={startDate}
                        onChange={(date) => setStartDate(date)}
                        format="MM/dd/yyyy"
                        inputVariant="outlined"
                        size="small"
                        label="Start Date"
                        style={{ minWidth: 170, flexShrink: 0 }}
                    />
                    <KeyboardDatePicker
                        value={endDate}
                        onChange={(date) => setEndDate(date)}
                        format="MM/dd/yyyy"
                        inputVariant="outlined"
                        size="small"
                        label="End Date"
                        style={{ minWidth: 170, flexShrink: 0 }}
                    />
                    <SearchField
                        variant="outlined"
                        size="small"
                        placeholder="Search properties..."
                        value={searchText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setSearchText(e.target.value)
                        }
                        style={{ minWidth: 200 }}
                    />
                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 200 }}
                    >
                        <InputLabel>Filter by Tags</InputLabel>
                        <Select
                            multiple
                            value={selectedTagIds}
                            onChange={(e) =>
                                setSelectedTagIds(e.target.value as number[])
                            }
                            label="Filter by Tags"
                            renderValue={(selected) => (
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 4,
                                    }}
                                >
                                    {(selected as number[]).map((tagId) => {
                                        const tag = userTags.find(
                                            (t) => t.id === tagId,
                                        )
                                        return tag ? (
                                            <Chip
                                                key={tagId}
                                                label={tag.name}
                                                size="small"
                                                style={{
                                                    backgroundColor: tag.color,
                                                    color: 'white',
                                                }}
                                            />
                                        ) : null
                                    })}
                                </Box>
                            )}
                            disabled={tagsLoading}
                        >
                            {userTags.map((tag) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    <Chip
                                        label={tag.name}
                                        size="small"
                                        style={{
                                            backgroundColor: tag.color,
                                            color: 'white',
                                            marginRight: theme.spacing(1),
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </Container>

            {/* Portfolio Table */}
            {loading ? (
                <Container flex={1} alignItems="center" justifyContent="center">
                    <CircularProgress
                        size={100}
                        style={{ marginTop: theme.spacing(10) }}
                    />
                </Container>
            ) : (
                <PortfolioTable
                    summaryList={filteredSummaryList}
                    onSelectProperty={handleSelectProperty}
                    onExport={(id, name) =>
                        exportApartmentDetail(id, name, buildDateParams())
                    }
                    onAnalyze={handleAnalyze}
                />
            )}

            {/* Detail Modal */}
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={modalOpen}>
                    <Paper
                        style={{
                            padding: theme.spacing(3),
                            width: '95vw',
                            maxWidth: 1400,
                            height: '80vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 8,
                        }}
                    >
                        {/* Modal Header */}
                        <Container
                            style={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            <Typography
                                variant="h5"
                                style={{
                                    ...typographyStyle(theme.typography.h5),
                                    fontWeight: 600,
                                }}
                            >
                                {detail?.apartment_name}
                            </Typography>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing(1),
                                }}
                            >
                                {detail && (
                                    <>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={
                                                <LoriIcon
                                                    width={18}
                                                    height={18}
                                                    color="#fff"
                                                />
                                            }
                                            onClick={() =>
                                                getAiSummary(
                                                    detail.apartment_id,
                                                    buildDateParams(),
                                                )
                                            }
                                            disabled={
                                                aiSummaryLoading ||
                                                detail.survey_count === 0
                                            }
                                            style={{
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                color: '#fff',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderRadius: 6,
                                                padding: theme.spacing(0.5, 2),
                                            }}
                                        >
                                            {aiSummaryLoading
                                                ? 'Analyzing...'
                                                : 'Analyze with Lori'}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<GetApp />}
                                            onClick={() =>
                                                exportApartmentDetail(
                                                    detail.apartment_id,
                                                    detail.apartment_name,
                                                    buildDateParams(),
                                                )
                                            }
                                            style={{
                                                backgroundColor: '#217346',
                                                color: '#fff',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderRadius: 6,
                                                padding: theme.spacing(0.5, 2),
                                            }}
                                        >
                                            Export to Excel
                                        </Button>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setQuestionConfigOpen(true)
                                            }
                                            style={{
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 6,
                                            }}
                                        >
                                            <Settings fontSize="small" />
                                        </IconButton>
                                    </>
                                )}
                                <IconButton
                                    onClick={handleCloseModal}
                                    size="small"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        </Container>

                        {/* Modal Search */}
                        {detail && detail.surveys.length > 0 && (
                            <Container
                                style={{
                                    padding: theme.spacing(1, 0),
                                }}
                            >
                                <SearchField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Search by unit, tenant, or comment..."
                                    value={modalSearchText}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) => setModalSearchText(e.target.value)}
                                    style={{ width: 320 }}
                                />
                            </Container>
                        )}

                        {/* AI Summary Card */}
                        {(aiSummaryLoading || aiSummary) && (
                            <Paper
                                variant="outlined"
                                style={{
                                    padding: theme.spacing(2),
                                    marginBottom: theme.spacing(1),
                                    borderRadius: 8,
                                    borderColor: aiSummary
                                        ? sentimentColor(
                                              aiSummary.overall_sentiment,
                                          )
                                        : theme.palette.grey[300],
                                    borderWidth: 2,
                                    flexShrink: 0,
                                }}
                            >
                                {aiSummaryLoading ? (
                                    <Container
                                        style={{
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: theme.spacing(1),
                                            padding: theme.spacing(2, 0),
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            Lori is analyzing survey
                                            responses...
                                        </Typography>
                                    </Container>
                                ) : aiSummary ? (
                                    <div>
                                        <Container
                                            style={{
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            <Container
                                                style={{
                                                    alignItems: 'center',
                                                    gap: theme.spacing(1),
                                                }}
                                            >
                                                <LoriIcon
                                                    width={22}
                                                    height={22}
                                                    color={
                                                        theme.palette.primary
                                                            .main
                                                    }
                                                />
                                                <Typography
                                                    variant="subtitle1"
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    Lori&apos;s Analysis
                                                </Typography>
                                                <Chip
                                                    label={
                                                        aiSummary.overall_sentiment
                                                            ? aiSummary.overall_sentiment
                                                                  .charAt(0)
                                                                  .toUpperCase() +
                                                              aiSummary.overall_sentiment.slice(
                                                                  1,
                                                              )
                                                            : 'Unknown'
                                                    }
                                                    size="small"
                                                    style={{
                                                        backgroundColor: sentimentColor(
                                                            aiSummary.overall_sentiment,
                                                        ),
                                                        color: '#fff',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </Container>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setAiSummary(null)
                                                }
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Container>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                            }}
                                        >
                                            {aiSummary.summary}
                                        </Typography>
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: theme.spacing(3),
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {aiSummary.strengths?.length >
                                                0 && (
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 200,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: 600,
                                                            color:
                                                                theme.palette
                                                                    .success
                                                                    .main,
                                                            marginBottom: 4,
                                                        }}
                                                    >
                                                        Strengths
                                                    </Typography>
                                                    <ul
                                                        style={{
                                                            margin: 0,
                                                            paddingLeft: 18,
                                                        }}
                                                    >
                                                        {aiSummary.strengths.map(
                                                            (s, i) => (
                                                                <li key={i}>
                                                                    <Typography variant="body2">
                                                                        {s}
                                                                    </Typography>
                                                                </li>
                                                            ),
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                            {aiSummary.concerns?.length > 0 && (
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 200,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: 600,
                                                            color:
                                                                theme.palette
                                                                    .error.main,
                                                            marginBottom: 4,
                                                        }}
                                                    >
                                                        Concerns
                                                    </Typography>
                                                    <ul
                                                        style={{
                                                            margin: 0,
                                                            paddingLeft: 18,
                                                        }}
                                                    >
                                                        {aiSummary.concerns.map(
                                                            (c, i) => (
                                                                <li key={i}>
                                                                    <Typography variant="body2">
                                                                        {c}
                                                                    </Typography>
                                                                </li>
                                                            ),
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                            <div
                                                style={{
                                                    flex: 1,
                                                    minWidth: 200,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontWeight: 600,
                                                        color:
                                                            theme.palette
                                                                .primary.main,
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    Recommendation
                                                </Typography>
                                                <Typography variant="body2">
                                                    {aiSummary.recommendation}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </Paper>
                        )}

                        {/* Modal Body */}
                        <div
                            style={{
                                overflowY: 'auto',
                                flex: 1,
                            }}
                        >
                            <SurveyDetailContent
                                detail={detail}
                                loading={detailLoading}
                                searchText={modalSearchText}
                            />
                        </div>
                    </Paper>
                </Slide>
            </Modal>

            {/* Standalone AI Summary Modal */}
            <Modal
                open={aiModalOpen}
                onClose={handleCloseAiModal}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={aiModalOpen}>
                    <Paper
                        style={{
                            padding: 0,
                            width: '90vw',
                            maxWidth: 800,
                            height: 520,
                            borderRadius: 12,
                            outline: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: theme.spacing(2, 3),
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                background: theme.palette.primary.main,
                                color: '#fff',
                                flexShrink: 0,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing(1.5),
                                }}
                            >
                                <LoriIcon width={28} height={28} color="#fff" />
                                <div>
                                    <Typography
                                        variant="subtitle1"
                                        style={{
                                            fontWeight: 700,
                                            color: '#fff',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        Lori&apos;s Analysis
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        style={{
                                            color: 'rgba(255,255,255,0.8)',
                                        }}
                                    >
                                        {aiModalPropertyName}
                                    </Typography>
                                </div>
                            </div>
                            <IconButton
                                size="small"
                                onClick={handleCloseAiModal}
                                style={{ color: '#fff' }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </div>

                        {/* Body */}
                        <div
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: theme.spacing(2.5, 3),
                            }}
                        >
                            {aiSummaryLoading ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        gap: theme.spacing(2),
                                    }}
                                >
                                    <CircularProgress size={36} />
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Lori is analyzing survey responses...
                                    </Typography>
                                </div>
                            ) : aiSummary ? (
                                <>
                                    {/* Sentiment + Summary */}
                                    <div
                                        style={{
                                            marginBottom: theme.spacing(2.5),
                                        }}
                                    >
                                        <Chip
                                            label={
                                                aiSummary.overall_sentiment
                                                    ? aiSummary.overall_sentiment
                                                          .charAt(0)
                                                          .toUpperCase() +
                                                      aiSummary.overall_sentiment.slice(
                                                          1,
                                                      )
                                                    : 'Unknown'
                                            }
                                            size="small"
                                            style={{
                                                backgroundColor: sentimentColor(
                                                    aiSummary.overall_sentiment,
                                                ),
                                                color: '#fff',
                                                fontWeight: 600,
                                                marginBottom: theme.spacing(1),
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            style={{
                                                lineHeight: 1.6,
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        >
                                            {aiSummary.summary}
                                        </Typography>
                                    </div>

                                    {/* Strengths & Concerns side by side */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: theme.spacing(2),
                                            marginBottom: theme.spacing(2.5),
                                        }}
                                    >
                                        {aiSummary.strengths?.length > 0 && (
                                            <Paper
                                                variant="outlined"
                                                style={{
                                                    flex: 1,
                                                    padding: theme.spacing(
                                                        1.5,
                                                        2,
                                                    ),
                                                    borderRadius: 8,
                                                    borderColor:
                                                        theme.palette.success
                                                            .main,
                                                    borderLeftWidth: 3,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontWeight: 700,
                                                        color:
                                                            theme.palette
                                                                .success.main,
                                                        marginBottom: 6,
                                                    }}
                                                >
                                                    Strengths
                                                </Typography>
                                                {aiSummary.strengths.map(
                                                    (s, i) => (
                                                        <Typography
                                                            key={i}
                                                            variant="body2"
                                                            style={{
                                                                lineHeight: 1.7,
                                                            }}
                                                        >
                                                            &bull;&ensp;{s}
                                                        </Typography>
                                                    ),
                                                )}
                                            </Paper>
                                        )}
                                        {aiSummary.concerns?.length > 0 && (
                                            <Paper
                                                variant="outlined"
                                                style={{
                                                    flex: 1,
                                                    padding: theme.spacing(
                                                        1.5,
                                                        2,
                                                    ),
                                                    borderRadius: 8,
                                                    borderColor:
                                                        theme.palette.error
                                                            .main,
                                                    borderLeftWidth: 3,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontWeight: 700,
                                                        color:
                                                            theme.palette.error
                                                                .main,
                                                        marginBottom: 6,
                                                    }}
                                                >
                                                    Concerns
                                                </Typography>
                                                {aiSummary.concerns.map(
                                                    (c, i) => (
                                                        <Typography
                                                            key={i}
                                                            variant="body2"
                                                            style={{
                                                                lineHeight: 1.7,
                                                            }}
                                                        >
                                                            &bull;&ensp;{c}
                                                        </Typography>
                                                    ),
                                                )}
                                            </Paper>
                                        )}
                                    </div>

                                    {/* Recommendation */}
                                    {aiSummary.recommendation && (
                                        <Paper
                                            variant="outlined"
                                            style={{
                                                padding: theme.spacing(1.5, 2),
                                                borderRadius: 8,
                                                borderColor:
                                                    theme.palette.primary.main,
                                                borderLeftWidth: 3,
                                                backgroundColor: `${theme.palette.primary.main}08`,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    fontWeight: 700,
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                Recommendation
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                style={{ lineHeight: 1.6 }}
                                            >
                                                {aiSummary.recommendation}
                                            </Typography>
                                        </Paper>
                                    )}
                                </>
                            ) : (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        No analysis available.
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </Paper>
                </Slide>
            </Modal>

            {/* Question Config Modal */}
            {detail && (
                <QuestionConfigModal
                    open={questionConfigOpen}
                    onClose={() => setQuestionConfigOpen(false)}
                    apartmentId={detail.apartment_id}
                    apartmentName={detail.apartment_name}
                />
            )}
        </Container>
    )
}
